// src/helpers/rutaFinancieras.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE ||
  "https://backre-miami.us-3.evennode.com";

const BASE_URL =
  String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// normaliza rutas: "financieras" => "/financieras"
const p = (path) =>
  `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () =>
  localStorage.getItem("token") || "";

const auth = () => {
  const token = getToken();

  return token
    ? {
        headers: {
          token,
        },
      }
    : {};
};

const debug =
  String(
    process.env.REACT_APP_DEBUG_API || ""
  ).toLowerCase() === "true";

const logUrl = (
  url,
  params,
  data,
  method = "GET"
) => {
  if (!debug) return;

  console.log(
    "👉 METHOD:",
    method
  );

  console.log(
    "👉 URL:",
    API.getUri({
      url,
      params,
    })
  );

  if (data) {
    console.log(
      "📦 DATA ENVIADA:",
      data
    );
  }
};

async function req(
  method,
  url,
  {
    params,
    data,
    signal,
  } = {}
) {
  logUrl(
    url,
    params,
    data,
    method
  );

  try {
    const res =
      await API.request({
        method,
        url,
        params,
        data,
        signal,
        ...auth(),
      });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log(
        "🧯 API ERROR:",
        msg
      );

      console.log(
        "🧯 STATUS:",
        err?.response?.status
      );

      console.log(
        "🧯 DATA:",
        err?.response?.data
      );
    }

    throw err;
  }
}

/* =========================================================
   NORMALIZADORES GENERALES
========================================================= */

const clean = (v) =>
  String(v ?? "").trim();

const toNum = (
  v,
  def = 0
) => {
  const n = Number(v);

  return Number.isFinite(n)
    ? n
    : def;
};

const toBool = (
  v,
  def = false
) => {
  if (
    v === undefined ||
    v === null ||
    v === ""
  ) {
    return def;
  }

  if (
    typeof v === "boolean"
  ) {
    return v;
  }

  if (
    v === 1 ||
    v === "1" ||
    String(v).toLowerCase() ===
      "true"
  ) {
    return true;
  }

  if (
    v === 0 ||
    v === "0" ||
    String(v).toLowerCase() ===
      "false"
  ) {
    return false;
  }

  return def;
};

const normalizarFee = (
  valor,
  def = 0
) => {
  const fee =
    toNum(
      valor,
      def
    );

  return Math.min(
    99.99,
    Math.max(
      0,
      fee
    )
  );
};

const TIPOS_CALCULO = [
  "MONTO_DIRECTO",
  "GROSS_UP",
  "POR_CUOTA",
  "FONDEO_INVERSO"
];

const normalizarTipoCalculo = (
  valor,
  def = "GROSS_UP"
) => {
  const tipo =
    clean(valor).toUpperCase();

  return TIPOS_CALCULO.includes(
    tipo
  )
    ? tipo
    : def;
};

const normalizarReglasCuotas = (
  reglas = []
) => {
  if (
    !Array.isArray(reglas)
  ) {
    return [];
  }

  const mapa =
    new Map();

  reglas.forEach(
    (regla) => {
      const cuotas =
        Number(
          regla?.cuotas
        );

      if (
        !Number.isFinite(
          cuotas
        ) ||
        cuotas <= 0
      ) {
        return;
      }

      mapa.set(
        cuotas,
        {
          cuotas,

          feePorcentaje:
            normalizarFee(
              regla?.feePorcentaje,
              0
            ),
        }
      );
    }
  );

  return Array.from(
    mapa.values()
  ).sort(
    (a, b) =>
      a.cuotas -
      b.cuotas
  );
};

/* =========================================================
   UNWRAP RESPUESTAS
========================================================= */

const unwrapFinanciera = (
  data
) => {
  if (!data) {
    return null;
  }

  if (
    data?.financiera
  ) {
    return data.financiera;
  }

  if (
    data?.data
  ) {
    return data.data;
  }

  return data;
};

const unwrapFinancieras = (
  data
) => {
  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(
      data?.financieras
    )
  ) {
    return data.financieras;
  }

  if (
    Array.isArray(
      data?.data
    )
  ) {
    return data.data;
  }

  if (
    Array.isArray(
      data?.docs
    )
  ) {
    return data.docs;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  if (
    Array.isArray(
      data?.items
    )
  ) {
    return data.items;
  }

  if (
    Array.isArray(
      data?.financieras?.data
    )
  ) {
    return data.financieras.data;
  }

  return [];
};

/* =========================================================
   PAYLOAD LEGACY / OPERATIVO

   IMPORTANTE:
   Este normalizador conserva compatibilidad
   con el administrador actual.

   NO agrega por defecto fee/cuotas/configuración,
   para evitar pisar accidentalmente valores nuevos.
========================================================= */

export const normalizarFinancieraPayload = (
  payload = {}
) => {
  const src =
    payload || {};

  return {
    ...src,

    codfinanciera:
      src.codfinanciera,

    financiera:
      clean(
        src.financiera ??
          src.nombre
      ),

    razonsocial:
      clean(
        src.razonsocial
      ),

    cuit:
      clean(
        src.cuit
      ),

    domicilio:
      clean(
        src.domicilio
      ),

    telefono1:
      clean(
        src.telefono1 ??
          src.telefono
      ),

    email:
      clean(
        src.email
      ),

    contacto:
      clean(
        src.contacto
      ),

    observaciones:
      clean(
        src.observaciones
      ),

    activo:
      src.activo ??
      true,
  };
};

/* =========================================================
   PAYLOAD NUEVO ADMINISTRADOR

   Este sí prepara:
   - fee
   - tipo cálculo
   - cuotas
   - estado
========================================================= */

export const normalizarFinancieraAdminPayload = (
  payload = {}
) => {
  const src =
    payload || {};

  let requiereCuotas =
    toBool(
      src.requiereCuotas,
      false
    );

  let tipoCalculo =
    normalizarTipoCalculo(
      src.tipoCalculo,
      requiereCuotas
        ? "POR_CUOTA"
        : "GROSS_UP"
    );

  if (
    tipoCalculo ===
    "POR_CUOTA"
  ) {
    requiereCuotas =
      true;
  }

  if (
    !requiereCuotas &&
    tipoCalculo ===
      "POR_CUOTA"
  ) {
    tipoCalculo =
      "GROSS_UP";
  }

  const reglasCuotas =
    requiereCuotas
      ? normalizarReglasCuotas(
          src.reglasCuotas
        )
      : [];

  const resultado = {
    codfinanciera:
      src.codfinanciera,

    financiera:
      clean(
        src.financiera ??
          src.nombre
      ),

    razonsocial:
      clean(
        src.razonsocial
      ),

    cuit:
      clean(
        src.cuit
      ),

    domicilio:
      clean(
        src.domicilio
      ),

    telefono1:
      clean(
        src.telefono1 ??
          src.telefono
      ),

    email:
      clean(
        src.email
      ),

    contacto:
      clean(
        src.contacto
      ),

    observaciones:
      clean(
        src.observaciones
      ),

    activo:
      toBool(
        src.activo,
        true
      ),

    feePorcentaje:
      normalizarFee(
        src.feePorcentaje,
        0
      ),

    tipoCalculo,

    requiereCuotas,

    aplicaTax:
      toBool(
        src.aplicaTax,
        true
      ),

    reglasCuotas,
  };

  /*
    configuracionPagosActiva es técnica.

    El nuevo administrador NO necesita mostrarla.

    Solamente la mandamos si expresamente
    viene en el payload.
  */
  if (
    Object.prototype.hasOwnProperty.call(
      src,
      "configuracionPagosActiva"
    )
  ) {
    resultado.configuracionPagosActiva =
      toBool(
        src.configuracionPagosActiva,
        false
      );
  }

  return resultado;
};

/* =========================================================
   FINANCIERAS OPERATIVAS ACTUALES

   GET /financieras
   Sigue devolviendo solamente activas.
========================================================= */

export async function getFinancieras({
  page = 1,
  limit = 50,
  q = "",
  signal,
} = {}) {
  const data =
    await req(
      "GET",
      p("financieras"),
      {
        params: {
          page,
          limit,
          q: String(
            q || ""
          ).trim(),
        },

        signal,
      }
    );

  const financieras =
    unwrapFinancieras(
      data
    );

  return {
    financieras,

    total:
      Number(
        data?.total ??
          financieras.length ??
          0
      ),

    page:
      Number(
        data?.page ??
          page
      ),

    limit:
      Number(
        data?.limit ??
          limit
      ),
  };
}

/* =========================================================
   FINANCIERA POR ID
========================================================= */

export async function getFinancieraById(
  id,
  {
    signal,
  } = {}
) {
  const data =
    await req(
      "GET",
      p(
        `financieras/${id}`
      ),
      {
        signal,
      }
    );

  return unwrapFinanciera(
    data
  );
}

/* =========================================================
   CRUD ACTUAL / COMPATIBLE
========================================================= */

export async function postFinanciera(
  payload,
  {
    signal,
  } = {}
) {
  const data =
    normalizarFinancieraPayload(
      payload
    );

  if (debug) {
    console.log(
      "🟢 postFinanciera PAYLOAD NORMALIZADO:",
      data
    );
  }

  return req(
    "POST",
    p("financieras"),
    {
      data,
      signal,
    }
  );
}

export async function putFinanciera(
  id,
  payload,
  {
    signal,
  } = {}
) {
  const data =
    normalizarFinancieraPayload(
      payload
    );

  if (debug) {
    console.log(
      "🟣 putFinanciera ID:",
      id
    );

    console.log(
      "🟣 putFinanciera PAYLOAD ORIGINAL:",
      payload
    );

    console.log(
      "🟢 putFinanciera PAYLOAD NORMALIZADO:",
      data
    );

    console.log(
      "🌐 putFinanciera URL:",
      `${BASE_URL}${p(
        `financieras/${id}`
      )}`
    );
  }

  return req(
    "PUT",
    p(
      `financieras/${id}`
    ),
    {
      data,
      signal,
    }
  );
}

export async function deleteFinanciera(
  id,
  {
    signal,
  } = {}
) {
  return req(
    "DELETE",
    p(
      `financieras/${id}`
    ),
    {
      signal,
    }
  );
}

/* =========================================================
   ADMINISTRADOR NUEVO
========================================================= */

// GET /financieras/admin/listado
//
// estado:
// TODAS
// ACTIVAS
// INACTIVAS
export async function getFinancierasAdmin({
  page = 1,
  limit = 100,
  q = "",
  estado = "TODAS",
  signal,
} = {}) {
  const data =
    await req(
      "GET",
      p(
        "financieras/admin/listado"
      ),
      {
        params: {
          page,

          limit,

          q: String(
            q || ""
          ).trim(),

          estado:
            String(
              estado ||
                "TODAS"
            )
              .trim()
              .toUpperCase(),
        },

        signal,
      }
    );

  const financieras =
    unwrapFinancieras(
      data
    );

  return {
    financieras,

    resumen: {
      activas:
        Number(
          data?.resumen
            ?.activas ||
            0
        ),

      inactivas:
        Number(
          data?.resumen
            ?.inactivas ||
            0
        ),

      conFee:
        Number(
          data?.resumen
            ?.conFee ||
            0
        ),

      conCuotas:
        Number(
          data?.resumen
            ?.conCuotas ||
            0
        ),
    },

    total:
      Number(
        data?.total ??
          financieras.length ??
          0
      ),

    page:
      Number(
        data?.page ??
          page
      ),

    limit:
      Number(
        data?.limit ??
          limit
      ),
  };
}

/* =========================================================
   CREAR DESDE NUEVO ADMINISTRADOR
========================================================= */

export async function postFinancieraAdmin(
  payload,
  {
    signal,
  } = {}
) {
  const data =
    normalizarFinancieraAdminPayload(
      payload
    );

  if (debug) {
    console.log(
      "🟢 postFinancieraAdmin:",
      data
    );
  }

  const respuesta =
    await req(
      "POST",
      p("financieras"),
      {
        data,
        signal,
      }
    );

  return {
    ...respuesta,

    financiera:
      unwrapFinanciera(
        respuesta
      ),
  };
}

/* =========================================================
   MODIFICAR DESDE NUEVO ADMINISTRADOR
========================================================= */

export async function putFinancieraAdmin(
  id,
  payload,
  {
    signal,
  } = {}
) {
  const data =
    normalizarFinancieraAdminPayload(
      payload
    );

  if (debug) {
    console.log(
      "🟣 putFinancieraAdmin ID:",
      id
    );

    console.log(
      "🟢 DATA:",
      data
    );
  }

  const respuesta =
    await req(
      "PUT",
      p(
        `financieras/${id}`
      ),
      {
        data,
        signal,
      }
    );

  return {
    ...respuesta,

    financiera:
      unwrapFinanciera(
        respuesta
      ),
  };
}

/* =========================================================
   ACTIVAR / DESACTIVAR

   Para activar usamos PUT.
   Para desactivar podemos usar PUT también,
   evitando confundirlo con eliminación física.
========================================================= */

export async function cambiarEstadoFinanciera(
  id,
  activo,
  {
    signal,
  } = {}
) {
  const respuesta =
    await req(
      "PUT",
      p(
        `financieras/${id}`
      ),
      {
        data: {
          activo:
            Boolean(
              activo
            ),
        },

        signal,
      }
    );

  return {
    ...respuesta,

    financiera:
      unwrapFinanciera(
        respuesta
      ),
  };
}

/* =========================================================
   LOGO FINANCIERA
========================================================= */

// POST /financieras/:id/logo
//
// FormData:
// archivo = File
export async function subirLogoFinanciera(
  id,
  archivo,
  {
    signal,
  } = {}
) {
  if (!archivo) {
    throw new Error(
      "Debe seleccionar un archivo."
    );
  }

  const formData =
    new FormData();

  formData.append(
    "archivo",
    archivo
  );

  if (debug) {
    console.log(
      "🖼️ subirLogoFinanciera:",
      {
        id,
        nombre:
          archivo?.name,
        tipo:
          archivo?.type,
        size:
          archivo?.size,
      }
    );
  }

  const respuesta =
    await req(
      "POST",
      p(
        `financieras/${id}/logo`
      ),
      {
        data:
          formData,

        signal,
      }
    );

  return {
    ...respuesta,

    financiera:
      unwrapFinanciera(
        respuesta
      ),
  };
}

/* =========================================================
   ELIMINAR LOGO
========================================================= */

export async function eliminarLogoFinanciera(
  id,
  {
    signal,
  } = {}
) {
  const respuesta =
    await req(
      "DELETE",
      p(
        `financieras/${id}/logo`
      ),
      {
        signal,
      }
    );

  return {
    ...respuesta,

    financiera:
      unwrapFinanciera(
        respuesta
      ),
  };
}

/* =========================================================
   FINANCIERA POR CÓDIGO
========================================================= */

export async function getFinancieraByCodigo(
  codfinanciera,
  {
    signal,
  } = {}
) {
  const codigo =
    String(
      codfinanciera || ""
    ).trim();

  if (!codigo) {
    return null;
  }

  const data =
    await req(
      "GET",
      p(
        `financieras/codigo/${codigo}`
      ),
      {
        signal,
      }
    );

  return unwrapFinanciera(
    data
  );
}

/* =========================================================
   BUSCADOR
========================================================= */

// Compatible con:
// buscarFinancieras({ q: "macro", limit: 20 })
// buscarFinancieras("macro", 20)

export async function buscarFinancieras(
  arg1 = {},
  arg2 = 20
) {
  let q = "";
  let limit = 20;
  let signal;

  if (
    typeof arg1 === "string" ||
    typeof arg1 === "number"
  ) {
    q =
      String(
        arg1 || ""
      ).trim();

    limit =
      Number(
        arg2 || 20
      );
  } else {
    q =
      String(
        arg1?.q || ""
      ).trim();

    limit =
      Number(
        arg1?.limit || 20
      );

    signal =
      arg1?.signal;
  }

  if (
    !q ||
    q.length < 2
  ) {
    return [];
  }

  const data =
    await req(
      "GET",
      p(
        "financieras/buscar"
      ),
      {
        params: {
          q,
          limit,
        },

        signal,
      }
    );

  const financieras =
    unwrapFinancieras(
      data
    );

  if (debug) {
    console.log(
      "🔎 buscarFinancieras q:",
      q
    );

    console.log(
      "🔎 financieras encontradas:",
      financieras
    );
  }

  return financieras;
}